import type { AlertColor, SnackbarCloseReason } from '@mui/material';
import type { ReactNode, SyntheticEvent } from 'react';

import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

export type PageAlertMessagePosition = 'bottom-right' | 'top-center';

type PageAlertMessageProps = {
  autoHideDuration?: number | null;
  message: ReactNode;
  onClose: () => void;
  open: boolean;
  position: PageAlertMessagePosition;
  severity: AlertColor;
  testId?: string;
};

const anchorOrigins = {
  'bottom-right': { vertical: 'bottom', horizontal: 'right' },
  'top-center': { vertical: 'top', horizontal: 'center' },
} as const;

export function PageAlertMessage({
  autoHideDuration = null,
  message,
  onClose,
  open,
  position,
  severity,
  testId,
}: Readonly<PageAlertMessageProps>) {
  const handleClose = (
    _event?: Event | SyntheticEvent,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    onClose();
  };

  return (
    <Snackbar
      anchorOrigin={anchorOrigins[position]}
      autoHideDuration={autoHideDuration ?? undefined}
      onClose={handleClose}
      open={open}
      sx={{ zIndex: (theme) => theme.zIndex.snackbar + 1 }}
    >
      <Alert
        data-testid={testId}
        onClose={handleClose}
        severity={severity}
        sx={{ alignItems: 'center', minWidth: 320, width: '100%' }}
        variant="filled"
      >
        {message}
      </Alert>
    </Snackbar>
  );
}
